import { SyntheticEvent } from "react";
import Button from "@mui/material/Button";
import Icon from "../NGMaterialIcon/NGMaterialIcon";
import { Box, Paper } from "@mui/material";
import "./NGReviewDialog.css";
import { setupHandlers, setupLocalState, updateItemContext } from "../../library/dataService";
import { useSignal } from "@preact/signals-react";
import { INGReviewDialogProps, RuntimeContext } from "../../library/NGFieldExtensions";
import { IActionTrigger } from "../../../resolvers-types";
import NGAudioRecorder from "../NGAudioRecorder/NGAudioRecorder";
import { isNil } from "lodash-es";
import { getTestId } from "../../library/utils";
import { log } from "../../library/logger";

const tag = "NGReviewDialog";

export default function NGReviewDialog({ config, context }: INGReviewDialogProps) {
  const currentContext = updateItemContext(context, config);
  const local = setupLocalState(
    config,
    {
      Visible: useSignal(config.Visible ?? true),
      Enabled: useSignal(config.Enabled ?? true),
      Data: useSignal(config.Data ?? {}),
    },
    currentContext
  );

  const handlers = setupHandlers(config, currentContext);

  function isReactionLike() {
    return local.Data.value.Reaction?.toLocaleLowerCase() == "like";
  }
  function isReactionDislike() {
    return local.Data.value.Reaction?.toLocaleLowerCase() == "dislike";
  }
  function onReactionLikeClick(e) {
    local.Data.value = { ...local.Data.value, Reaction: "Like" };

    if (handlers["onReviewAction"]) {
      handlers["onReviewAction"](e, local.Data.value);
    }
  }

  function onReactionDislikeClick(e) {
    local.Data.value = { ...local.Data.value, Reaction: "Dislike" };
    if (handlers["onReviewAction"]) {
      handlers["onReviewAction"](e, local.Data.value);
    }
  }

  return (
    <>
      {local.Visible.value && (
        <Paper className="reviewContainer" elevation={3}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {config.ShowReaction && (
              <>
                <Button
                  data-testid={getTestId(config, "ThumbsUp")}
                  className="button"
                  startIcon={<Icon name="thumb_up" />}
                  color={isReactionLike() ? "success" : "primary"}
                  onClick={onReactionLikeClick}
                />
                <Button
                  data-testid={getTestId(config, "ThumbsDown")}
                  className="button"
                  startIcon={<Icon name="thumb_down" />}
                  color={isReactionDislike() ? "error" : "primary"}
                  onClick={onReactionDislikeClick}
                />
              </>
            )}
            {config.ShowTextNotes && (
              <Button
                data-testid={getTestId(config, "CreateTextNote")}
                className="button"
                startIcon={<Icon name="rate_review" />}
                onClick={(e) => {
                  if (handlers["onCreateTextNote"]) handlers["onCreateTextNote"](e, local.Data.value);
                }}
              />
            )}
            {config.ShowVoiceNotes && (
              <NGAudioRecorder
                config={{
                  DestinationBucket: "reviews",
                  DisplayPlayerAfterRecording: false,
                  Actions: [
                    {
                      Trigger: "onStopRecording",
                      preHandler: (
                        handlerName: string,
                        action: IActionTrigger,
                        e: SyntheticEvent,
                        data: object,
                        context: RuntimeContext
                      ) => {
                        log.info(tag, "onStopRecording", e, data, context);

                        // data has the file name
                        if (isNil(data)) return;

                        local.Data.value.FileName = data;

                        if (handlers["onCreateVoiceNote"]) handlers["onCreateVoiceNote"](e, local.Data.value);
                      },
                    },
                  ],
                }}
                context={currentContext}
              />
            )}
            {config.ShowComments && (
              <>
                <Button
                  className="button"
                  startIcon={<Icon name="comment" />}
                  data-testid={getTestId(config, "ShowComments")}
                />
              </>
            )}
            {config.ShowInfo && (
              <Button
                data-testid={getTestId(config, "ShowInfo")}
                className="button"
                startIcon={<Icon name="info" />}
                onClick={(e) => {
                  if (handlers["onShowInfo"]) handlers["onShowInfo"](e, local.Data.value);
                }}
              />
            )}
            <Button
              data-testid={getTestId(config, "ShowHistory")}
              className="button"
              startIcon={<Icon name="history" />}
              onClick={(e) => {
                if (handlers["onShowHistory"]) handlers["onShowHistory"](e, local.Data.value);
              }}
            />
          </Box>
          <span className="provideFeedbackTextReviewDialog" data-testid={getTestId(config, "FeedbackText")}>
            Feedback for {local.Data.value.Item?.Name}{" "}
          </span>
        </Paper>
      )}
    </>
  );
}
